import { useEffect, useMemo, useRef } from 'react'

import cn from 'classnames'
import { Loader2 } from 'lucide-react'

import { useWalletModal as useTronWalletModal } from '@tronweb3/tronwallet-adapter-react-ui'

import CircleSwapIcon from '@src/components/icons/CircleSwapIcon'
import LoadingV2 from '@src/components/icons/LoadingV2'
import { ChainId } from '@src/configs/chainInfo'
import { StepNames } from '@src/enums'
import { useSwapButtonState } from '@src/hooks/useSwapButtonState'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useAuthContext } from '@src/providers/AuthProvider'
import { useEmbed } from '@src/providers/EmbedProvider'
import { analyticsService } from '@src/services/analytics'
import { IExchangeData } from '@src/types/exchange'
import { FeatherQuoteError } from '@src/types/feather'
import { IQuoteResponse } from '@src/types/order'
import { isSourceStableAsset } from '@src/utils/tokenUtils'
import { useApplicationStore } from '@src/zustand_stores/application.store'

interface IErrors {
    isInsufficientBalance?: boolean
    isQuoteInvalid?: boolean
    isBuyOutputLoading?: boolean
    isDestinationSolana?: boolean
    isOriginSolana?: boolean
    isTransactionCapped?: boolean
    isLiquidityUnavailable?: boolean
    hasInsufficientGas?: boolean
}

interface Props extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    errorsJSON?: IErrors
    sellData: IExchangeData
    buyData?: IExchangeData
    sellAmountString?: string
    isCustomRecipientWallet?: boolean
    destinationAddress?: string
    liquiditySource?: string
    isOrderRejected?: boolean
    isQuoteExpired?: boolean
    currentStep?: StepNames
    hasInsufficientGas?: boolean
    quoteResponse?: IQuoteResponse
    isGasEstimationFetched?: boolean
    waitingForConnectedWalletInteraction?: boolean
    isBuyChainWalletConnected?: boolean
    isSellChainWalletConnected?: boolean
    isQuoteLoading?: boolean
    isQuoteFetching?: boolean
    buttonText?: string
    liquidityAvailable?: boolean
    isCheckingLiquidity?: boolean
    liquidityError?: string | null
    featherQuoteError?: FeatherQuoteError | null
    onMetaMaskPopup?: (step: StepNames) => void
}

export const SwapButton = ({
    errorsJSON,
    sellData,
    buyData,
    className,
    buttonText = 'Swap',
    isCustomRecipientWallet = false,
    destinationAddress = '',
    liquiditySource,
    isOrderRejected = false,
    isQuoteExpired = false,
    currentStep = StepNames.UNSTARTED,
    hasInsufficientGas = false,
    quoteResponse = undefined,
    onMetaMaskPopup,
    isGasEstimationFetched,
    sellAmountString = undefined,
    featherQuoteError = null,
    liquidityAvailable = true,
    isCheckingLiquidity = false,
    liquidityError = null,
    disabled,
    ...props
}: Props) => {
    const { waitingForConnectedWalletInteraction } = useApplicationStore()
    const {
        isSolanaConnected,
        isTronConnected,
        isEVMConnected,
        EVMChainId,
        solanaWalletAddress,
        tronWalletAddress,
        evmWalletAddress,
    } = useWalletConnections()
    const { connectHandler: connectEVMOrSolana } = useAuthContext()
    const { applyPrimaryColorToButton, primaryColor, isEmbed } = useEmbed()

    // Create a ref to the button element
    const buttonRef = useRef<HTMLButtonElement>(null)
    // Create refs for SVG elements
    const svgRefs = useRef<(SVGSVGElement | null)[]>([])

    // For Tron wallet connection
    const { setVisible: openTronWalletModal } = useTronWalletModal()

    // Use the analytics service

    // Connect wallet function that handles different wallet types
    const connectWallet = (chainId: ChainId) => {
        try {
            // Track the main wallet connection attempt
            const walletType = chainId === ChainId.Tron ? 'Tron' : chainId === ChainId.Solana ? 'Solana' : 'EVM'

            // Track the main wallet connection attempt event
            analyticsService.trackEvent('wallet_main_attempt_connect', {
                wallet_type: walletType,
                button_location: 'swap_screen', // This is the main swap button
                timestamp: new Date().toISOString(),
            })

            // Note: Specific wallet connection attempts (MetaMask, Phantom, etc.)
            // will be tracked in their respective connection handlers

            if (chainId === ChainId.Tron) {
                // For Tron, open the wallet modal
                openTronWalletModal(true)
            } else {
                // For EVM and Solana, use the AuthProvider
                connectEVMOrSolana()
            }
        } catch (error) {
            // Track the wallet connection failure
            const walletName =
                chainId === ChainId.Tron ? 'TronLink' : chainId === ChainId.Solana ? 'Phantom' : 'MetaMask'
            const chainType = chainId === ChainId.Tron ? 'TRX' : chainId === ChainId.Solana ? 'SVM' : 'EVM'

            analyticsService.trackWalletConnectionFailed(error, walletName, chainType)

            // Re-throw the error to maintain the original behavior
            throw error
        }
    }

    const isBuyChainWalletConnected = useMemo(() => {
        if (!buyData) {
            return true // Skip check if buyData isn't available
        }

        switch (buyData.chainId) {
            case ChainId.Solana:
                return isSolanaConnected
            case ChainId.Tron:
                return isTronConnected
            default:
                return isEVMConnected
        }
    }, [buyData, isSolanaConnected, isTronConnected, isEVMConnected, EVMChainId])

    const isSellChainWalletConnected = useMemo(() => {
        switch (sellData.chainId) {
            case ChainId.Solana:
                return isSolanaConnected
            case ChainId.Tron:
                return isTronConnected
            default:
                return isEVMConnected
        }
    }, [sellData, isSolanaConnected, isTronConnected, isEVMConnected])

    // Destructure errors from errorsJSON
    const { isInsufficientBalance = false, isQuoteInvalid = false, isBuyOutputLoading = false } = errorsJSON || {}

    // Use the shared hook for button state
    const {
        buttonDisabled: calculatedButtonDisabled,
        buttonText: buttonTextValue,
        isPendingState,
        needsWalletConnection,
        getWalletConnectionText,
        isAddRecipientCase,
    } = useSwapButtonState({
        sellData,
        buyData,
        sellAmountString,
        isInsufficientBalance,
        hasInsufficientGas,
        isBuyOutputLoading,
        isQuoteInvalid,
        isCustomRecipientWallet,
        destinationAddress,
        liquiditySource,
        isOrderRejected,
        isQuoteExpired,
        currentStep,
        quoteResponse,
        isGasEstimationFetched,
        waitingForConnectedWalletInteraction,
        isBuyChainWalletConnected,
        isSellChainWalletConnected,
        isQuoteLoading: props.isQuoteLoading,
        isQuoteFetching: props.isQuoteFetching,
        isLiquidityUnavailable: !liquidityAvailable, // Derive from liquidityAvailable prop
        isCheckingLiquidity,
        liquidityError,
    })

    // Check if the source amount is from a stable asset
    const isFromStableAsset = useMemo(() => isSourceStableAsset(sellData), [sellData])

    // Final disabled state, combining calculated state with prop override
    // Note: isLiquidityUnavailable is already included in buttonDisabled from the hook
    const finalButtonDisabled =
        disabled !== undefined ? disabled : calculatedButtonDisabled || isPendingState || !liquidityAvailable

    // Get button text color from CSS variable in embed mode
    useEffect(() => {
        if (!isEmbed || !buttonRef.current) return

        // Get the button text color from CSS variable
        const buttonTextColor =
            getComputedStyle(document.documentElement).getPropertyValue('--color-button-text').trim() || '#FFFFFF'

        // Apply text color to the button text
        if (buttonRef.current) {
            // Apply to button text
            buttonRef.current.style.color = buttonTextColor

            // Find and color all SVG elements inside the button
            const svgElements = buttonRef.current.querySelectorAll('svg')
            svgElements.forEach(svg => {
                // Set the color of the SVG using currentColor
                svg.style.color = buttonTextColor

                // Check if this is a CircleSwapIcon or stroke-based SVG
                const isStrokeBased = svg.querySelector('path[stroke]:not([stroke="none"])') !== null

                if (isStrokeBased) {
                    // For stroke-based SVGs, don't modify the fill
                    // Only apply to stroke attributes explicitly
                    const strokeElements = svg.querySelectorAll('[stroke]:not([stroke="none"])')
                    strokeElements.forEach(elem => {
                        if (elem.getAttribute('stroke') === 'currentColor') {
                            // Already using currentColor, which will inherit from parent
                            return
                        }
                        elem.setAttribute('stroke', buttonTextColor)
                    })
                } else {
                    // For fill-based SVGs, apply fill to relevant elements
                    // 1. Elements with 'fill' attribute
                    const fillElements = svg.querySelectorAll('[fill]:not([fill="none"])')
                    fillElements.forEach(elem => {
                        elem.setAttribute('fill', buttonTextColor)
                    })

                    // 2. Path elements without stroke (common in icons)
                    const paths = svg.querySelectorAll('path:not([stroke]):not([fill="none"])')
                    paths.forEach(path => {
                        path.setAttribute('fill', buttonTextColor)
                    })

                    // 3. Circle elements without stroke
                    const circles = svg.querySelectorAll('circle:not([stroke]):not([fill="none"])')
                    circles.forEach(circle => {
                        circle.setAttribute('fill', buttonTextColor)
                    })

                    // 4. Rect elements without stroke
                    const rects = svg.querySelectorAll('rect:not([stroke]):not([fill="none"])')
                    rects.forEach(rect => {
                        rect.setAttribute('fill', buttonTextColor)
                    })
                }

                // Special handling for loading spinners
                if (svg.classList.contains('animate-spin')) {
                    // Force color on all elements with stroke
                    const allStrokeElements = svg.querySelectorAll('[stroke]:not([stroke="none"])')
                    allStrokeElements.forEach(el => {
                        el.setAttribute('stroke', buttonTextColor)
                    })

                    // Only apply fill to elements without stroke that have fill
                    const allFillElements = svg.querySelectorAll('[fill]:not([fill="none"]):not([stroke])')
                    allFillElements.forEach(el => {
                        el.setAttribute('fill', buttonTextColor)
                    })
                }
            })
        }
    }, [isEmbed, finalButtonDisabled, buttonTextValue])

    // Apply styling from EmbedProvider when primary color changes or button mounts
    useEffect(() => {
        if (buttonRef.current && primaryColor) {
            applyPrimaryColorToButton(buttonRef.current)
        }
    }, [primaryColor, applyPrimaryColorToButton, finalButtonDisabled]) // Re-apply when button is disabled/enabled

    const buttonClass = cn(
        // Use the custom-wallet-button class when button is for wallet connection
        buttonTextValue.includes('Connect')
            ? 'custom-wallet-button'
            : 'flex select-none items-center justify-center space-x-[10px] rounded-md px-[18px] py-[12px] font-medium leading-7 transition-all',
        // Only apply these background styles when not in wallet connection mode
        !buttonTextValue.includes('Connect') && {
            // Show the disabled style for error states, Add Recipient case, and when liquidity is unavailable
            'bg-mach-component-background':
                (finalButtonDisabled && !buttonTextValue.includes('Connect')) || isAddRecipientCase,
            // Show blue button for all enabled buttons (including "Swap")
            'bg-mach-primary hover:brightness-110 swap-button-main':
                (!finalButtonDisabled || buttonTextValue.includes('Connect')) && !isAddRecipientCase,
        },
        // Add a special class for targeting in embed mode
        isEmbed && 'embed-swap-button',
        className
    )

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        // Handle wallet connection if needed
        if (needsWalletConnection) {
            const walletType = getWalletConnectionText

            // If we need a custom recipient address, don't try to connect a wallet
            // Instead let the parent component handle showing the address input
            if (walletType === 'Add Recipient') {
                // Don't prevent the event in this case - we want the parent handler to run
                return
            }

            // Otherwise we're connecting a wallet, so prevent the event from propagating
            event.stopPropagation()
            event.preventDefault()

            if (walletType && walletType.includes('Solana')) {
                connectWallet(ChainId.Solana)
                return
            } else if (walletType && walletType.includes('Tron')) {
                connectWallet(ChainId.Tron)
                return
            } else if (walletType && walletType.includes('EVM')) {
                connectWallet(ChainId.Ethereum)
                return
            }
            return // Early return for any wallet connection
        }

        // Handle MetaMask popups for various operations
        if (currentStep === StepNames.DEPOSIT_WETH) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.DEPOSIT_WETH_PENDING)
        } else if (currentStep === StepNames.APPROVE_TOKENS) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.APPROVE_TOKENS_PENDING)
        } else if (currentStep === StepNames.CONFIRM_SWAP) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.CONFIRM_SWAP_PENDING)
        }

        // We don't call props.onClick here anymore since we're using combinedOnClick
    }

    // Extract onClick from props to prevent it from overriding our handler
    const { onClick: propsOnClick, ...otherProps } = props

    // Create a combined onClick handler
    const combinedOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        // First run our internal handler
        handleButtonClick(event)

        // Track if the source is a stable asset when the user clicks the swap button
        // Only track this for actual swap actions, not for wallet connections
        if (!needsWalletConnection && !isPendingState && sellData && buttonTextValue === 'Swap') {
            analyticsService.trackEvent('swap_attempt', {
                is_source_stable_asset: isFromStableAsset,
                source_token_symbol: sellData.token.symbol,
                source_token_chain: sellData.chainId,
                source_amount_usd: sellAmountString,
                timestamp: new Date().toISOString(),
            })

            // Also trigger Twitter conversion event at swap initiation
            // This ensures the pixel is recognized even when the URL changes during the quote process
            if (typeof window !== 'undefined' && window.twq && sellData.token && buyData?.token) {
                // Calculate USD values if available
                const sourceAmountUSD = sellData.token?.price_current
                    ? Number(sellAmountString) * Number(sellData.token.price_current)
                    : 0

                // Get connected wallet address based on chain type
                let walletAddress = ''
                if (sellData.chainId === ChainId.Solana && isSolanaConnected) {
                    // For Solana, use the solana wallet address
                    walletAddress = solanaWalletAddress?.toString() || ''
                } else if (sellData.chainId === ChainId.Tron && isTronConnected) {
                    // For Tron, use the tron wallet address
                    walletAddress = tronWalletAddress || ''
                } else if (isEVMConnected) {
                    // For EVM chains
                    walletAddress = evmWalletAddress || ''
                }

                // Create a unique ID for this swap attempt
                const swapAttemptId = `swap_${sellData.token.symbol}_${buyData.token.symbol}_${Date.now()}`

                // Fire Twitter conversion event for swap attempt
                window.twq('event', 'tw-ppzhv-psjk0', {
                    value: sourceAmountUSD,
                    currency: 'USD',
                    email_address: walletAddress,
                    conversion_id: swapAttemptId,
                    contents: [
                        {
                            content_type: 'crypto_token',
                            content_id: sellData.token.symbol,
                            content_name: sellData.token.symbol,
                            content_price: sellData.token?.price_current || 0,
                            num_items: Number(sellAmountString) || 1,
                            content_group_id: String(sellData.chainId),
                        },
                        {
                            content_type: 'crypto_token',
                            content_id: buyData.token.symbol,
                            content_name: buyData.token.symbol,
                            content_price: buyData.token?.price_current || 0,
                            num_items: 1,
                            content_group_id: String(buyData.chainId),
                        },
                    ],
                })

                console.log('[Twitter Conversion] Fired swap attempt event', {
                    sourceToken: sellData.token.symbol,
                    destinationToken: buyData.token.symbol,
                    sourceAmountUSD,
                })
            }
        }

        // If event wasn't prevented and we have a props onClick, call it
        if (!event.defaultPrevented && propsOnClick) {
            propsOnClick(event)
        }
    }

    return (
        <button
            ref={buttonRef}
            className={buttonClass}
            disabled={finalButtonDisabled && !buttonTextValue.includes('Connect')}
            onClick={combinedOnClick}
            data-swap-button='true'
            data-stable-source={isFromStableAsset}
            {...otherProps}
        >
            <div className='flex items-center justify-center gap-2'>
                {isPendingState && (
                    <Loader2
                        className='h-5 w-5 animate-spin'
                        ref={el => {
                            if (svgRefs.current) svgRefs.current[0] = el
                        }}
                    />
                )}
                {buttonTextValue.includes('Connect') && (
                    <>
                        {buttonTextValue.includes('EVM') && (
                            <img src={'assets/network/ethereum.png'} alt='evm' className='h-6 w-6' />
                        )}
                        {buttonTextValue.includes('Solana') && (
                            <img src={'assets/network/solana.png'} alt='solana' className='h-6 w-6' />
                        )}
                        {buttonTextValue.includes('Tron') && (
                            <img src={'assets/network/tron.png'} alt='tron' className='h-6 w-6' />
                        )}
                    </>
                )}
                {((props.isQuoteLoading && buttonTextValue === 'Getting exchange rate') ||
                    buttonTextValue === 'Simulating gas costs') && (
                    <LoadingV2
                        className='h-5 w-5 animate-spin'
                        ref={el => {
                            if (svgRefs.current) svgRefs.current[1] = el
                        }}
                    />
                )}
                {buttonText === 'Swap' ? (
                    <div className='flex items-center gap-1.5 font-bold'>
                        {buttonTextValue === 'Swap' && (
                            <CircleSwapIcon
                                className='h-5 w-5'
                                ref={el => {
                                    if (svgRefs.current) svgRefs.current[2] = el
                                }}
                            />
                        )}
                        <span className='text-sm !leading-6'>{buttonTextValue}</span>
                    </div>
                ) : (
                    <span className='!leading-6'>{buttonText}</span>
                )}
            </div>
        </button>
    )
}
